import type * as React from "react";

import {
  Clock,
  LoaderCircle,
  type LucideIcon,
} from "@/shared/foundation/icons";
import { StatusBadge, type StatusTone } from "@/shared/foundation/primitives";

import { type ToolCallStatus } from "../liveToolCalls";

/**
 * Only a call in flight gets a badge. A finished call reads as done from its
 * "Used" wording, and a failure badge would draw the eye to failed calls.
 */
const STATUS_LOOK: Record<
  ToolCallStatus,
  { tone: StatusTone; icon: LucideIcon; label: string }
> = {
  pending: { tone: "neutral", icon: Clock, label: "Pending" },
  running: { tone: "info", icon: LoaderCircle, label: "Running" },
};

interface ToolCallStatusBadgeProps {
  status: ToolCallStatus;
  /**
   * What a running call reports it is doing, such as "Starting the Python
   * sandbox". Shown in place of "Running"; ignored while the call is pending.
   */
  activity?: string | null;
}

/** An in-flight tool call's status as a pill, named "Status: …" for assistive tech. */
export function ToolCallStatusBadge({
  status,
  activity,
}: ToolCallStatusBadgeProps): React.JSX.Element {
  const look = STATUS_LOOK[status];
  const label = status === "running" && activity ? activity : look.label;
  return (
    <StatusBadge
      tone={look.tone}
      icon={look.icon}
      label={label}
      aria-label={`Status: ${label}`}
    />
  );
}
